// Filtrado, orden, píldoras y métricas del explorer de Catálogo del portal
// (`portal-vendedor-catalogo.html`). Funciones puras: la pantalla solo compone.
//
// La búsqueda reusa el plegado del paso 03 (`catalogFilters.ts`) para que los dos catálogos
// encuentren lo mismo con las mismas letras. Lo que NO se comparte es el estado: el mock del portal
// filtra por mínimo/máximo y financiera y ordena por columna, y el del paso 03 por banda y select.
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { coincide, terminosDe } from '@/domains/sales/wizard/catalogFilters';
import { isService, isSoldOut, stockCount } from '@/domains/sales/wizard/catalogAvailability';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { productKindOf, type ProductKind } from '@/shared/patterns/KindChip';
import type { StockAlertKey } from '@/shared/patterns/StockAlertChip';
import { PRODUCT_CATEGORY_LABEL } from '@/shared/lib/format/productCategory';
import { productTierLabel } from '@/shared/lib/format/productTier';
import { canonicalFinancier } from '../../pos/lib/financierChips';
import { SaleFinancierEnum } from '@fiado/type-kit/bin/retailWizard/enums/SaleFinancierEnum';
import { variantLabel } from '@/shared/lib/format/capacity';

/** Dónde recuerda el navegador si la columna de filtros va colapsada, la misma llave del mock. */
export const FILTERS_COLLAPSED_KEY = 'bo_filters_collapsed';

/** Umbral de «bajo stock» del mock: de 1 a 10 unidades. En 0 el equipo está agotado. */
export const LOW_STOCK_MAX = 10;

/** Prioridad desde la que un equipo cuenta como «Top ventas», como el `priority >= 70` del mock. */
export const TOP_SALES_PRIORITY_MIN = 70;

export interface CatalogExplorerFilters {
    query: string;
    /** Marca exacta; `''` es «todas». */
    brand: string;
    /** Precio en PESOS tal como se teclea; `''` es sin tope. */
    minPrice: string;
    maxPrice: string;
    /** Financiera alternativa que tiene que aceptar el equipo; `''` es cualquiera. */
    financier: string;
}

export const EMPTY_FILTERS: CatalogExplorerFilters = {
    query: '',
    brand: '',
    minPrice: '',
    maxPrice: '',
    financier: '',
};

/** Cuántos filtros están puestos — es el contador del encabezado del panel. */
export function countActiveFilters(filters: CatalogExplorerFilters): number {
    return [
        filters.query.trim(),
        filters.brand,
        filters.minPrice,
        filters.maxPrice,
        filters.financier,
    ].filter((value) => value !== '').length;
}

/** Las píldoras de la barra de stock: las de Inventario con fuente más la de promoción. */
export type StatFilter = 'all' | 'inStock' | 'low' | 'out' | 'promo';

export type SortKey = 'sku' | 'brand' | 'model' | 'price' | 'stock' | 'priority';
export type SortDirection = 'asc' | 'desc';

export interface SortState {
    key: SortKey;
    direction: SortDirection;
}

/** El mock abre ordenado por prioridad de venta, de mayor a menor. */
export const DEFAULT_SORT: SortState = { key: 'priority', direction: 'desc' };

/** Un clic en la columna activa invierte; en otra, empieza ascendente. */
export function nextSort(current: SortState, key: SortKey): SortState {
    if (current.key !== key) return { key, direction: 'asc' };
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

/** Marcas presentes en el catálogo, alfabéticas: el filtro ofrece lo que existe. */
export function brandOptions(items: CatalogItem[]): string[] {
    return [...new Set(items.map((item) => item.brand))]
        .filter((brand) => brand !== '')
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** Financieras que aparecen en el catálogo, alfabéticas: el filtro ofrece lo que existe. */
export function financierOptions(items: CatalogItem[]): string[] {
    return [...new Set(items.flatMap((item) => item.eligibleFinanciers))]
        .filter((financier) => financier !== '')
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** Pesos tecleados → centavos. Vacío o basura queda sin tope, no en cero. */
function priceLimitCents(raw: string, fallback: number): number {
    const pesos = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(pesos)) return fallback;
    return Math.round(pesos * 100);
}

export function isTopSeller(item: CatalogItem): boolean {
    return (item.salePriority ?? -1) >= TOP_SALES_PRIORITY_MIN;
}

export function hasPromotion(item: CatalogItem): boolean {
    return item.hasPromo || (item.promotions?.length ?? 0) > 0;
}

/** ¿El equipo pasa la píldora activa? */
function matchesStat(item: CatalogItem, stat: StatFilter): boolean {
    switch (stat) {
        case 'inStock':
            return hasStock(item);
        case 'promo':
            return hasPromotion(item);
        case 'low':
            return stockToneOf(item) === 'low';
        case 'out':
            return isSoldOut(item);
        default:
            return true;
    }
}

export function filterCatalog<T extends CatalogItem>(
    items: T[],
    filters: CatalogExplorerFilters,
    stat: StatFilter,
): T[] {
    const terms = terminosDe(filters.query);
    const minCents = priceLimitCents(filters.minPrice, 0);
    const maxCents = priceLimitCents(filters.maxPrice, Number.POSITIVE_INFINITY);

    return items.filter((item) => {
        if (!coincide(item, terms)) return false;
        if (filters.brand !== '' && item.brand !== filters.brand) return false;
        if (item.cashPriceCents < minCents || item.cashPriceCents > maxCents) return false;
        if (
            filters.financier !== '' &&
            !item.eligibleFinanciers.some(
                (code) => canonicalFinancier(code) === canonicalFinancier(filters.financier),
            )
        ) {
            return false;
        }
        return matchesStat(item, stat);
    });
}

const COMPARATORS: Record<SortKey, (a: CatalogItem, b: CatalogItem) => number> = {
    sku: (a, b) => a.sku.localeCompare(b.sku, 'es-MX'),
    brand: (a, b) =>
        a.brand.localeCompare(b.brand, 'es-MX') || a.model.localeCompare(b.model, 'es-MX'),
    model: (a, b) =>
        a.model.localeCompare(b.model, 'es-MX') || a.capacity.localeCompare(b.capacity, 'es-MX'),
    price: (a, b) => a.cashPriceCents - b.cashPriceCents,
    stock: (a, b) => stockCount(a) - stockCount(b),
    priority: (a, b) => (a.salePriority ?? -1) - (b.salePriority ?? -1),
};

/** Desempate de todo orden: alfabético por marca, con lo que no tiene marca al final. */
function byBrandBlankLast(a: CatalogItem, b: CatalogItem): number {
    const blank = (item: CatalogItem) => Number(item.brand.trim() === '');
    return blank(a) - blank(b) || COMPARATORS.brand(a, b);
}

/** Ordena sin mutar; con `soldOutLast` (el default) lo agotado baja al final en cualquier orden. */
export function sortCatalog<T extends CatalogItem>(
    items: T[],
    sort: SortState,
    { soldOutLast = true }: { soldOutLast?: boolean } = {},
): T[] {
    const compare = COMPARATORS[sort.key];
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...items].sort(
        (a, b) =>
            (soldOutLast ? Number(isSoldOut(a)) - Number(isSoldOut(b)) : 0) ||
            sign * compare(a, b) ||
            byBrandBlankLast(a, b),
    );
}

/** Los contadores de las píldoras, sobre todo el catálogo. */
export type CatalogStats = Record<StatFilter, number>;

/** Un producto con piezas en la tienda; un servicio no lleva inventario. */
export function hasStock(item: Pick<CatalogItem, 'itemType' | 'stock'>): boolean {
    return !isService(item) && (item.stock ?? 0) > 0;
}

export function catalogStats(items: CatalogItem[]): CatalogStats {
    return {
        all: items.length,
        inStock: items.filter(hasStock).length,
        promo: items.filter(hasPromotion).length,
        low: items.filter((item) => stockToneOf(item) === 'low').length,
        out: items.filter(isSoldOut).length,
    };
}

/** Las métricas del toolbar sobre lo que quedó visible. Sin filas no hay promedio que sacar. */
export interface CatalogMetrics {
    avgPriceCents: number | null;
    stockTotal: number | null;
    /** Productos con SureKeep entre sus financieras. */
    surekeepCount: number | null;
    /** Filas sobre las que se calculó, para el «N de M» de «Con SK». */
    count: number;
}

export function catalogMetrics(rows: CatalogItem[]): CatalogMetrics {
    if (rows.length === 0) {
        return { avgPriceCents: null, stockTotal: null, surekeepCount: null, count: 0 };
    }
    const total = rows.reduce((sum, item) => sum + item.cashPriceCents, 0);
    return {
        avgPriceCents: Math.round(total / rows.length),
        // El stock del kit sale de sus componentes, que ya tienen su fila: sumarlo lo contaría dos veces.
        stockTotal: rows
            .filter((item) => item.category !== 'BUNDLE')
            .reduce((sum, item) => sum + stockCount(item), 0),
        surekeepCount: rows.filter((item) =>
            item.eligibleFinanciers.includes(SaleFinancierEnum.SUREKEEP),
        ).length,
        count: rows.length,
    };
}

export type StockTone = 'ok' | 'low' | 'out';

/** Semáforo de inventario con los cortes del mock: agotado en 0, ámbar de 1 a 10. */
export function stockTone(stock: number): StockTone {
    if (stock <= 0) return 'out';
    if (stock <= LOW_STOCK_MAX) return 'low';
    return 'ok';
}

/** Tono de un SKU; un servicio no lleva inventario y siempre está disponible. */
export function stockToneOf(item: Pick<CatalogItem, 'itemType' | 'stock'>): StockTone {
    return isService(item) ? 'ok' : stockTone(item.stock ?? 0);
}

/** Texto del stock de un SKU; `unit` se agrega al número (p. ej. « unidades»). */
export function stockLabelOf(item: Pick<CatalogItem, 'itemType' | 'stock'>, unit = ''): string {
    if (isService(item)) return 'Servicio';
    return isSoldOut(item) ? 'Agotado' : `${String(item.stock ?? 0)}${unit}`;
}

/** Cuántos productos hay, en singular o plural. */
export function pluralProducts(count: number): string {
    return `${String(count)} ${count === 1 ? 'producto' : 'productos'}`;
}

/** Banda de comparables del mock: ±25% del precio del equipo abierto. */
export const SIMILAR_PRICE_SPREAD = 0.25;

/** Cuántos comparables se listan en la pestaña «Similares». */
export const SIMILAR_LIMIT = 5;

/** Los equipos de la misma banda de precio, del más parecido al menos. Nunca el mismo SKU. */
export function similarProducts(item: CatalogItem, items: CatalogItem[]): CatalogItem[] {
    const min = item.cashPriceCents * (1 - SIMILAR_PRICE_SPREAD);
    const max = item.cashPriceCents * (1 + SIMILAR_PRICE_SPREAD);
    return items
        .filter(
            (candidate) =>
                candidate.sku !== item.sku &&
                candidate.cashPriceCents >= min &&
                candidate.cashPriceCents <= max,
        )
        .sort(
            (a, b) =>
                Math.abs(a.cashPriceCents - item.cashPriceCents) -
                    Math.abs(b.cashPriceCents - item.cashPriceCents) ||
                a.brand.localeCompare(b.brand, 'es-MX'),
        )
        .slice(0, SIMILAR_LIMIT);
}

/** Nombre comercial del equipo, como lo lee el vendedor. */
export function productLabel(item: CatalogItem): string {
    return itemDisplayName(item);
}

/** El modelo; un kit o servicio sin marca ni modelo se nombra por su descripción o SKU. */
export function modelLabel(item: CatalogItem): string {
    if (item.model.trim() !== '') return item.model;
    return item.brand.trim() === '' ? itemDisplayName(item) : '';
}

/** Capacidad y color, la línea que distingue dos variantes del mismo modelo. */
export function productVariant(item: CatalogItem): string {
    return variantLabel(item);
}

/** Nombre de la promoción para la celda y el chip; «Promo» si el silo solo marca que hay una. */
export function promotionLabel(item: CatalogItem): string | null {
    const named = item.promotions?.find((promotion) => promotion.name.trim() !== '');
    if (named) return named.name;
    return item.hasPromo ? 'Promo' : null;
}

/** Colores del mismo modelo en el catálogo de la tienda, alfabéticos. */
export function availableColors(item: CatalogItem, items: CatalogItem[]): string[] {
    const sameModel = items.filter(
        (candidate) => candidate.brand === item.brand && candidate.model === item.model,
    );
    return [...new Set(sameModel.map((candidate) => candidate.color.trim()))]
        .filter((color) => color !== '')
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** «Smartphone gama media», como el `categoria_detalle` del mock; sin gama, solo «Smartphone». */
export function phoneCategoryLabel(item: CatalogItem): string {
    if (item.tier.trim() === '') return 'Smartphone';
    return `Smartphone ${productTierLabel(item.tier).toLowerCase()}`;
}

/** Categoría del producto: un teléfono con su gama; lo demás con el nombre de su categoría. */
export function categoryLabel(item: CatalogItem): string {
    const category = item.category ?? 'PHONE';
    return category === 'PHONE' ? phoneCategoryLabel(item) : PRODUCT_CATEGORY_LABEL[category];
}

/** «87 / 100»; `null` si el catálogo no trae prioridad para el equipo. */
export function priorityLabel(item: CatalogItem): string | null {
    return typeof item.salePriority === 'number' ? `${String(item.salePriority)} / 100` : null;
}

/** Tipo del producto para el chip de color; sin categoría, el catálogo viejo solo tenía teléfonos. */
export function catalogKindOf(item: Pick<CatalogItem, 'category' | 'itemType'>): ProductKind {
    return productKindOf(item.category ?? 'PHONE', item.itemType);
}

export interface CatalogAlert {
    key: StockAlertKey;
    label: string;
    /** La explicación completa, para el `title`. */
    text: string;
}

/** Alertas del producto de la más grave a la menos grave; «Top ventas» no va si está agotado. */
export function catalogAlerts(item: CatalogItem): CatalogAlert[] {
    const tone = stockToneOf(item);
    const alerts: CatalogAlert[] = [];
    if (tone === 'out') {
        alerts.push({ key: 'out', label: 'Sin stock', text: 'Sin piezas en tu tienda' });
    }
    if (tone === 'low') {
        const pieces = item.stock ?? 0;
        alerts.push({
            key: 'low',
            label: 'Stock bajo',
            text: `Stock bajo: ${String(pieces)} pieza${pieces === 1 ? '' : 's'} en tu tienda, ${String(LOW_STOCK_MAX)} o menos`,
        });
    }
    if (tone !== 'out' && isTopSeller(item)) {
        alerts.push({
            key: 'top',
            label: 'Top ventas',
            text: `Top ventas: prioridad de venta de ${String(TOP_SALES_PRIORITY_MIN)} o más en el catálogo`,
        });
    }
    return alerts;
}
